import { Button, Modal, Select } from "@mantine/core";
import { useState } from "react";
import type { ApiError } from "../../../../../api";
import { useFileOrigins } from "../../../../../hooks/useFileOrigins";
import { ModalFooter } from "../../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { getFieldSpecificError } from "../../../../../utils/api-utils";
import { getErrorText } from "../../../../../utils/get-error-text";
import { FormField } from "../../../../BuildingBlocks/Forms/MantineFormFields/FormField";
import { LoadOrError } from "../../../../LoadOrError/LoadOrError";
import type { EnergyDataImportRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { retryUploadWithOriginRequest } from "../../../EnergyDataUploadFlow/EnergyDataUpload";
import { GeneralImportDetailsList } from "../../EnergyDataImportDetailsModal/GeneralImportDetailsList";
import { useDataImportRequestDetails } from "../../hooks/useDataImportRequestDetails";

const NOT_PROVIDED_OPTION = { value: "notProvided", label: "Anderes" };
const DEFAULT_INPUT_ERROR = "Bitte mindestens zwei Zeichen eingeben";
const LENGTH_INPUT_ERROR = "Bitte geben Sie höchsten 200 Zeichen ein";

const isValidInput = (value: string | null) =>
  !!value && value.trim().length >= 2;

const isOverLengthInput = (value: string | null) =>
  !!(value && value.trim().length > 200);

interface OriginModalProps {
  dataImportRequest: EnergyDataImportRequest;
  onClose: () => void;
  onRetryUploadDone: (dataImportRequest: EnergyDataImportRequest) => void;
}

function OriginModal({
  dataImportRequest,
  onClose,
  onRetryUploadDone
}: OriginModalProps) {
  const { showErrorToast } = useToast();
  const { dataImportRequestWithDetails, isLoading } =
    useDataImportRequestDetails(dataImportRequest.id);

  const {
    origins,
    isLoading: isOriginsLoading,
    error: originsError
  } = useFileOrigins();

  const [selectedOrigin, setSelectedOrigin] = useState("");
  const [newOriginValue, setNewOriginValue] = useState("");
  const [isShowInput, setIsShowInput] = useState(false);
  const [inputErrorTexts, setInputErrorTexts] = useState<string[]>([]);
  const [submitIsDisabled, setSubmitIsDisabled] = useState(true);

  const originOptions = [
    NOT_PROVIDED_OPTION,
    ...origins.map((origin) => ({
      value: origin.id,
      label: origin.name
    }))
  ];

  function handleChangeOriginOption(value: string | null) {
    if (value === NOT_PROVIDED_OPTION.value) {
      setSelectedOrigin("");
      setIsShowInput(true);
      setSubmitIsDisabled(false);
    } else {
      setInputErrorTexts([]);
      setIsShowInput(false);
      setNewOriginValue("");

      if (value) {
        setSelectedOrigin(value);
        setSubmitIsDisabled(false);
      }
    }
  }

  function handleChangeOriginInput(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputErrorTexts([]);
    setNewOriginValue(value);

    if (isValidInput(value) && !isOverLengthInput(value)) {
      setSubmitIsDisabled(false);
    }
  }

  function showInputIfInvalidOriginValue(errorMsg: string) {
    setInputErrorTexts([errorMsg]);
    setSubmitIsDisabled(true);
  }

  function handleRetryUploadWithOrigin() {
    if (selectedOrigin) {
      setSubmitIsDisabled(false);
    } else if (isOverLengthInput(newOriginValue)) {
      showInputIfInvalidOriginValue(LENGTH_INPUT_ERROR);
      return;
    } else if (!isValidInput(newOriginValue)) {
      showInputIfInvalidOriginValue(DEFAULT_INPUT_ERROR);
      return;
    }

    retryUploadWithOriginRequest(
      dataImportRequest.id,
      selectedOrigin,
      newOriginValue
    )
      .then((response) => {
        onRetryUploadDone(response.data);
      })
      .catch((e: ApiError) => {
        const errorMessage = getFieldSpecificError(e, ["fileOriginId"]);
        const errorText = getErrorText(
          e,
          errorMessage ? { [e.response?.status as number]: errorMessage } : {}
        );
        showErrorToast(errorText);
      })
      .finally(() => {
        onClose();
      });
  }

  return (
    <Modal
      className="OriginModalModal"
      opened
      size="lg"
      title={`Importdetails: ${dataImportRequest.energyDataFile.name}`}
      onClose={onClose}
    >
      <GeneralImportDetailsList
        generalDetails={
          isLoading ? dataImportRequest : dataImportRequestWithDetails
        }
      />
      <p>
        Leider konnte nicht automatisch erkannt werden aus welchen Quellsystem
        die Datei stammt. Bitte wählen Sie das Quellsystem (das System aus dem
        die Datei exportiert wurde) aus. Dies hilft den Import zu ermöglichen
        oder zumindest mögliche Fehlerquellen einzugrenzen.
      </p>
      <LoadOrError error={originsError} loading={isOriginsLoading}>
        <Select
          data={originOptions}
          mb="sm"
          required
          onChange={(newValue) => handleChangeOriginOption(newValue)}
        />
        {isShowInput && (
          <form className="FormItems">
            <FormField
              error={
                inputErrorTexts.length > 0 ? inputErrorTexts[0] : undefined
              }
              helpText="Bitte geben Sie den Namen des Systems ein von dem Sie die Datei bezogen haben."
              label="Name des Quellsystems*"
              name="newOriginValue"
              type="text"
              value={newOriginValue}
              onChange={handleChangeOriginInput}
            />
          </form>
        )}
      </LoadOrError>
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          color="secondary"
          disabled={submitIsDisabled}
          onClick={handleRetryUploadWithOrigin}
        >
          Wiederholen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { OriginModal, OriginModalProps };
